// The conflict of interest disclosure form's editor (ADR 0029 §6 and its 2026-10-26 note):
// the rules an internal-use form keeps, what myBRE shows of it, and the
// draft taken from the Board Governance & Ethics Manual.
//
// The disclosure is a forms engine form (ADR 0011) with
// form.internal_use = 'governance_coi'. Its answers are stored on
// coi_disclosure, not form_submission, so the parts of the engine that only
// make sense for the public (uploads, marketing consent, linking a person by
// a contact field) can't be used on it.

import type { FormDefinition, FormField, FormFieldType } from './forms'
import { FORM_FIELD_TYPES } from './forms'

export type DisclosureFormVersionSummary = { version: number; title: string; publishedAt: string; publishedByName: string | null; sha256: string }

// GET /v1/governance/disclosure-form. `version` and `definition` are the
// published version directors answer now; `draft` is what the next one will
// be.
export type DisclosureFormView = {
  version: number
  definition: FormDefinition
  draft: FormDefinition
  draftUpdatedAt: string
  draftChanged: boolean
  versions: DisclosureFormVersionSummary[]
}

// The key of the certification box. myBRE clears this box (and only this
// one) when a director files an update, so each filing is certified anew.
export const COI_CERTIFY_KEY = 'certify'

// Field types a disclosure can use: everything but a file upload (filings
// have nowhere to keep files, and myBRE has no upload).
export const DISCLOSURE_FIELD_TYPES = FORM_FIELD_TYPES.filter((t) => t !== 'file') as Exclude<FormFieldType, 'file'>[]

// Field types myBRE's FormFields (bre-loyalty app/board/BoardActions.tsx)
// draws: a section as a heading, single choice as radio buttons, multiple
// choice and checkboxes as check boxes, long text as a text area and the
// rest as text inputs. A test holds this list against the disclosure rules.
export const MYBRE_RENDERED_FIELD_TYPES: readonly FormFieldType[] = ['section', 'short_text', 'long_text', 'email', 'phone', 'number', 'date', 'single_choice', 'multiple_choice', 'checkbox']

// What can't be saved on the disclosure form. Core refuses a draft (and a
// publish) with any of these; the editor shows them first.
export function disclosureFormProblems(def: FormDefinition): string[] {
  const problems: string[] = []
  for (const f of def.fields) {
    if (f.type === 'file') problems.push(`${f.label}: a disclosure can’t ask for a file`)
    if (f.role) problems.push(`${f.label}: a disclosure doesn’t link people or organizations, so a field can’t have a role`)
    if (f.type === 'checkbox' && f.purpose && f.purpose !== 'acknowledgement') problems.push(`${f.label}: a disclosure checkbox is an acknowledgement, never marketing consent`)
  }
  if (!def.fields.some((f) => f.type === 'checkbox' && f.required)) problems.push('Keep a required certification checkbox')
  return problems
}

// How myBRE will show the form differently from Hub's preview, so the ED
// can fix it before publishing. None of these stop a publish.
export function myBreRenderingNotes(def: FormDefinition): string[] {
  const notes: string[] = []
  for (const f of def.fields) {
    if (!MYBRE_RENDERED_FIELD_TYPES.includes(f.type)) notes.push(`${f.label}: myBRE can’t show this kind of question`)
    if (f.type === 'section' && f.help) notes.push(`${f.label}: myBRE shows a section’s heading but not the text under it. Put that text in the next question’s help text or in the introduction.`)
  }
  const certify = def.fields.find((f) => f.key === COI_CERTIFY_KEY)
  if (!certify || certify.type !== 'checkbox' || !certify.required) notes.push(`myBRE clears only the required checkbox with the key “${COI_CERTIFY_KEY}” when a director files an update. Without it, an update starts already certified from the last filing.`)
  return notes
}

// ------------------------------------------------- the manual's annual form

const yesNo = (key: string, label: string): FormField => ({ key, type: 'single_choice', label, options: ['Yes', 'No'], required: true, sensitive: true })
const ack = (key: string, label: string, help?: string): FormField => ({ key, type: 'checkbox', label, ...(help ? { help } : {}), required: true, sensitive: false, purpose: 'acknowledgement' })

// The Annual Conflict of Interest & Board Acknowledgement Form from the Board
// Governance & Ethics Manual (effective April 1, 2026, pages 11-12), as a
// DRAFT for the ED: the board has not adopted the manual, so it is loaded as
// the disclosure form's draft and never published by a script.
//
// Two changes from the manual, as its review recommended:
// - Question 2 asks about any ownership or investment interest, not a "35% or
//   greater" one.
// - The attendance line says only "I understand the Board Attendance Policy";
//   it doesn't promise 75% attendance, which the bylaws don't require.
//
// The manual's Name and Position lines are left out: Hub records who filed
// and their seat and office. Section headings carry no text of their own,
// because myBRE shows only the heading.
export const COI_FORM_MANUAL_DRAFT = {
  title: 'Annual conflict of interest and board acknowledgement',
  intro:
    'Every director files this once each fiscal year (July 1 to June 30). It is read only by the Executive Director and the Executive Committee, and is never shared with other directors or staff. If something changes during the year, file an update.',
  fields: [
    { key: 'part_1', type: 'section', label: 'Part 1: Conflict of interest disclosure', required: false, sensitive: false },
    yesNo('business_relationships', '1. Business relationships: Are you, or is a member of your immediate family, an officer, director, trustee, partner or employee of an entity that does business with BRE?'),
    yesNo('financial_interest', '2. Financial interest: Do you, or does a member of your immediate family, have any ownership or investment interest in an entity that provides goods or services to BRE?'),
    yesNo('gifts', '3. Gifts and favors: Have you received any gift, gratuity or favor from a person or entity that does business, or seeks to do business, with BRE (not counting gifts of nominal value under $50)?'),
    yesNo('other_conflicts', '4. Other potential conflicts: Are you aware of any other relationship or interest that could be seen as a conflict of interest under BRE’s Conflict of Interest Policy?'),
    {
      key: 'details',
      type: 'long_text',
      label: 'Details for each “Yes” above',
      help: 'Name the entity, who is involved and the relationship or interest. Leave blank if every answer is No.',
      required: false,
      sensitive: true,
    },
    { key: 'part_2', type: 'section', label: 'Part 2: Policy acknowledgements', required: false, sensitive: false },
    ack('coi_policy_received', 'I have received a copy of the Conflict of Interest Policy.'),
    ack('coi_policy_read', 'I have read and understand the Conflict of Interest Policy.'),
    ack('coi_policy_comply', 'I agree to comply with the Conflict of Interest Policy, and I understand that I have a continuing obligation to disclose any conflict that arises during the year.'),
    ack('whistleblower_ethics', 'I have received and read the Whistleblower Protection Policy and the Code of Ethics.'),
    ack('document_retention', 'I understand the Document Retention and Destruction Policy and agree to protect BRE’s records accordingly.'),
    ack('attendance_policy', 'I understand the Board Attendance Policy.'),
    { key: 'part_3', type: 'section', label: 'Part 3: Confidentiality agreement', required: false, sensitive: false },
    ack(
      'confidentiality',
      'I agree to keep BRE’s confidential information confidential.',
      'As a member of the Board, I will have access to information that is confidential and proprietary to BRE (including donor lists, non-public financial data and personnel records). I agree to maintain the confidentiality of this information and will not use it for personal gain or disclose it to unauthorized third parties.',
    ),
    { key: 'part_4', type: 'section', label: 'Part 4: Certification and signature', required: false, sensitive: false },
    ack(
      COI_CERTIFY_KEY,
      'I certify that the information above is true and complete to the best of my knowledge, and that I will promptly notify the Board President or the Secretary in writing if anything arises that may create a conflict of interest.',
    ),
    { key: 'signature', type: 'short_text', label: 'Signature: type your full name', help: 'Typing your name here signs this form.', required: true, sensitive: false, maxLength: 200 },
    { key: 'signed_on', type: 'date', label: 'Date', required: true, sensitive: false },
  ],
  confirmationMessage: 'Thank you. Your disclosure and acknowledgements are filed for this fiscal year.',
  submitLabel: 'File my disclosure',
} satisfies FormDefinition
